// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { addYears } from "date-fns";

import { WorkflowsResidentRecord } from "~datatypes";

import { workflowsUrl } from "../core/views";
import { JusticeInvolvedPersonBase } from "./JusticeInvolvedPersonBase";
import { PersonType } from "./types";

const LIFE_SENTENCE_THRESHOLD = addYears(new Date(), 200);

export class Resident extends JusticeInvolvedPersonBase<WorkflowsResidentRecord> {
  get profileUrl(): string {
    return workflowsUrl("residentProfile", {
      justiceInvolvedPersonId: this.pseudonymizedId,
    });
  }

  get facilityId(): string | undefined {
    return this.record.facilityId ?? undefined;
  }

  get gender(): string | undefined {
    return this.record.gender;
  }

  get unitId(): string | undefined {
    return this.record.unitId ?? undefined;
  }

  get combinedFacilityUnitId(): string | undefined {
    return (
      [this.facilityId, this.unitId].filter(Boolean).join(" / ") || "Unknown"
    );
  }

  get custodyLevel(): string | undefined {
    return this.record.custodyLevel ?? undefined;
  }

  get displayCustodyLevel(): string {
    if (this.stateCode === "US_AZ") {
      if (
        !this.custodyLevel ||
        this.custodyLevel === "INTERNAL_UNKNOWN" ||
        this.custodyLevel === "EXTERNAL_UNKNOWN"
      ) {
        return "UNKNOWN CUSTODY LEVEL";
      } else {
        return `${this.custodyLevel.replace("_", " ")} CUSTODY`;
      }
    }
    const tenantConfig = this.rootStore.tenantStore.currentTenantConfig;
    if (!this.custodyLevel) {
      return tenantConfig?.custodyLevelCopy?.["null"] ?? "Unknown";
    }
    return (
      tenantConfig?.custodyLevelCopy?.[this.custodyLevel] ?? this.custodyLevel
    );
  }

  get admissionDate(): Date | undefined {
    return this.record.admissionDate;
  }

  get releaseDate(): Date | undefined {
    const { releaseDate, metadata } = this.record;

    // US_AZ stores the release date (SED) only in metadata.sedDate
    if (metadata.stateCode === "US_AZ") {
      return metadata.sedDate;
    }

    return releaseDate;
  }

  get onLifeSentence(): boolean | undefined {
    const { releaseDate } = this.record;
    if (!releaseDate) return;

    return new Date(releaseDate) > LIFE_SENTENCE_THRESHOLD;
  }

  get systemConfig() {
    return this.rootStore.workflowsStore.systemConfigFor("INCARCERATION");
  }

  get usTnFacilityAdmissionDate(): Date | undefined {
    return this.record.usTnFacilityAdmissionDate;
  }

  get metadata() {
    return this.record.metadata;
  }

  get personType(): PersonType {
    return this.record.personType;
  }
}
